import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Download,
  ChevronRight,
  Bot,
  User,
  MessageSquare,
} from 'lucide-react';
import RefreshButton from '../../components/layout/RefreshButton';
import Select from '../../components/ui/Select';
import { adminApi } from '../../services/adminApi';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
];

export default function AgentList() {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);

  const fetchAgents = useCallback(async (searchQuery = '', status = '') => {
    try {
      const response = await adminApi.getAgents({
        page,
        limit: 20,
        search: searchQuery || undefined,
        status: status || undefined,
      });
      setAgents(response.agents || []);
      setTotalPages(response.totalPages || 1);
      setTotalAgents(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  }, [page]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchAgents(appliedSearch, statusFilter);
      setIsLoading(false);
    };
    loadData();
  }, [fetchAgents, appliedSearch, statusFilter]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAgents(appliedSearch, statusFilter);
    setIsRefreshing(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  // Clear search results when input is emptied
  useEffect(() => {
    if (searchInput === '' && appliedSearch !== '') {
      setPage(1);
      setAppliedSearch('');
    }
  }, [searchInput, appliedSearch]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Agents</h1>
          <p className="text-text-secondary mt-1">
            All agents across the platform ({totalAgents} total)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
          <button className="btn-secondary">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by agent name or owner email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input pr-10 w-full"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
          </div>
          <div className="min-w-[160px]">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
              placeholder="All Status"
            />
          </div>
          <button type="submit" className="btn-primary whitespace-nowrap">
            Search
          </button>
        </form>
      </div>

      {/* Agents Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Owner</th>
                <th>Status</th>
                <th>WhatsApp</th>
                <th>Conversations</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7}>
                      <div className="h-12 bg-bg-secondary rounded animate-pulse"></div>
                    </td>
                  </tr>
                ))
              ) : agents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-text-muted">
                    No agents found
                  </td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">
                            {agent.name}
                          </p>
                          <p className="text-sm text-text-muted">
                            {agent.businessType || 'AI Agent'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <User className="w-4 h-4" />
                        {agent.ownerEmail || '-'}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          agent.status === 'active'
                            ? 'badge-success'
                            : agent.status === 'paused'
                            ? 'badge-warning'
                            : 'badge-gray'
                        }`}
                      >
                        {agent.status}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          agent.whatsappStatus === 'connected' ? 'badge-success' : 'badge-gray'
                        }`}
                      >
                        {agent.whatsappStatus === 'connected' ? 'Connected' : 'Not Connected'}
                      </span>
                      {agent.whatsappStatus === 'connected' && agent.whatsappPhoneNumber && (
                        <p className="text-xs text-text-muted mt-1">{agent.whatsappPhoneNumber}</p>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-text-muted" />
                        <span>{agent.conversationsCount ?? 0}</span>
                      </div>
                    </td>
                    <td className="text-text-secondary">
                      {formatDate(agent.createdAt)}
                    </td>
                    <td>
                      <Link
                        to={`/agents/${agent.id}`}
                        className="p-2 rounded-lg hover:bg-bg-secondary text-text-muted hover:text-primary transition-colors inline-flex"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
